<template>
    <div>
        <div class="flex flex-col xl:gap-48 lg:gap-32 lg:flex-row-reverse lg:items-start lg:justify-between">
            <OrderDetails class="lg:flex-1" />

            <div class="col lg:flex-1">
                <AppAccordion
                    label="Create Account"
                    prefix="1."
                    :complete="store.$state.accountValidated"
                    v-model="store.$state.steps.createAccount.open"
                    ref="createAccountAccordion"
                >
                    <LoginStep
                        mode="new"
                        @complete="handleLoginComplete"
                    />
                </AppAccordion>

                <AppAccordion
                    label="Shipping & Payment"
                    prefix="2."
                    class="border-t-2 border-gray-400"
                    :disabled="!store.$state.accountValidated"
                    :complete="store.$state.steps.paymentMethod.complete"
                    v-model="store.$state.steps.paymentMethod.open"
                >
                    <StripeElements
                        v-if="store.$state.steps.paymentMethod.open"
                        :handler="paymentHandler"
                        :validatorFn="prePaymentValidator"
                        :paymentIntentFn="paymentIntentFn"
                        :disableCoupon="true"
                    >
                        <template #before-wallets>
                            <div class="text-base text-gray-700" id="shipping-info">
                                The headset typically arrives within 5-7 business days.
                            </div>
                        </template>
                    </StripeElements>
                </AppAccordion>
            </div>
        </div>

        <ProgressModal
            v-if="state.showProgressModal"
            :steps="steps"
            :loading="state.showProgressModal"
        >
            <template v-if="state.orderComplete">
                <p class="text-center my-6">
                    <template v-if="redirectCountDown >= 0">
                        You will be redirected in {{ redirectCountDown }} seconds
                    </template>
                    <template v-else> Redirecting you now... </template>
                </p>
                <a class="button button--primary" :href="orderCompletePage">
                    Click here to Get Started!
                </a>
            </template>
        </ProgressModal>
    </div>
</template>

<script setup lang="ts">
import { useCheckoutStore } from '@/store/checkout'
import { useUserStore } from '@/store/user'
import { useCartStore } from '@/store/cart'
import OrderDetails from '@/components/BundleDetails.vue'
import LoginStep from '@/components/Shared/LoginStep.vue'
import AppAccordion from '@/components/App/AppAccordion.vue'
import { isUniqueEmail } from '@/api/memberships'
import { bundlePurchase } from '@/api/bundles'
import { computed, nextTick, reactive, ref } from 'vue'
import { v4 as uuid } from 'uuid'
import ProgressModal from '@/components/Shared/ProgressModal.vue'
import StripeElements from '@/components/Shared/StripeElements.vue'
import { trackOrder } from '@/utils/trackers'
import { setupIntent } from '@/api/setupIntent'

const store = useCheckoutStore()
const userStore = useUserStore()
const cart = useCartStore()
const state = reactive({
    paymentComplete: false,
    subscriptionComplete: false,
    equipmentComplete: false,
    trackingComplete: false,
    finalizingComplete: false,
    orderComplete: false,
    trackingResponse: {
        tracked: [] as any[],
        errors: [] as unknown[],
    },
    redirectInterval: null as number | null,
    showProgressModal: false,
})

const redirectCountDown = ref(5)
const createAccountAccordion = ref()
const orderCompletePage = computed(() => {
    const plan = cart.selectedPlan?.group_key || ''
    const interval = cart.selectedPlan?.billing_interval || ''
    return `/membership-purchased/?plan=${plan}&billing=${interval}&bundle=${cart.$state.bundle?.bundle_group_key}`
})

const handleLoginComplete = () => {
    store.$patch({
        steps: {
            selectEquipment: {
                complete: true,
            },
            paymentMethod: {
                open: true,
            },
        },
    })
}

const steps = computed((): ProgressStep[] => {
    const steps: ProgressStep[] = [
        {
            name: 'Verifying payment',
            status: state.paymentComplete ? 'complete' : 'current',
        },
        {
            name: 'Processing order',
            status: !state.paymentComplete
                ? 'upcoming'
                : state.subscriptionComplete
                    ? 'complete'
                    : 'current',
        },
        {
            name: 'Creating login credentials',
            status: !state.equipmentComplete
                ? 'upcoming'
                : state.trackingComplete
                    ? 'complete'
                    : 'current',
        },
        {
            name: 'Order complete',
            status: !state.finalizingComplete
                ? 'upcoming'
                : state.orderComplete
                    ? 'complete'
                    : 'current',
        },
    ]
    return steps
})

const resetProgress = () => {
    state.paymentComplete = false
    state.equipmentComplete = false
    state.trackingComplete = false
    state.finalizingComplete = false
    state.orderComplete = false
    state.trackingResponse = {
        tracked: [] as any[],
        errors: [] as unknown[],
    }
    state.redirectInterval = null
    state.showProgressModal = false
}

const redirect = () => {
    window.location.href = orderCompletePage.value
}

const finalizeOrder = () => new Promise((resolve) => {
    setTimeout(() => {
        state.trackingComplete = true
        setTimeout(() => {
            state.finalizingComplete = true
            setTimeout(() => {
                state.orderComplete = true
                state.redirectInterval = window.setInterval(() => {
                    if (redirectCountDown.value <= 1) {
                        if (state.redirectInterval) {
                            clearInterval(state.redirectInterval)
                        }
                        state.showProgressModal = false
                        redirect()
                    }
                    redirectCountDown.value--
                }, 1000)
                resolve(true)
            }, 350)
        }, 250)
    }, 150)
})

const paymentIntentFn = async (params: Partial<PaymentIntentParams>) => {
    const checkoutParams = store.getCheckoutParams()
    const response: PaymentIntentResponse = await setupIntent({
        ...params,
        wc_id: cart.$state.bundle?.wc_id,
        subscription: cart.$state.bundle?.plan?.stripe_price_id,
        is_bundle: true,
        amount: cart.totals.total,
        description: cart.$state.bundle?.description,
        shipping: checkoutParams.shipping,
        address: checkoutParams.address,
    })
    store.$patch({
        paymentIntent: {
            id: response.id,
            client_secret: response.client_secret,
            payment_method: response.payment_method || store.$state.paymentIntent?.payment_method,
        },
    })
}

const handleTracking = async (membershipOrder: any, equipmentOrder: any) => {
    const { tracked, errors } = trackOrder(membershipOrder, equipmentOrder)
    state.trackingResponse.tracked = tracked || []
    state.trackingResponse.errors = errors || []
}

const prePaymentValidator = async (): Promise<boolean> => {
    const isUnique = await isUniqueEmail(store.$state.email)
    if (!isUnique) {
        throw new Error('Your email address is already in use. Please contact support@winreality.com')
    }

    if (!store.$state.accountValidated) {
        // open create account accordion
        store.$state.steps.createAccount.open = true
        await nextTick()
        // scroll to createAccountAccordion ref
        window.scrollTo({
            top: createAccountAccordion.value.$el.offsetTop,
            behavior: 'smooth',
        })
        throw new Error('Please re-validate your account credentials')
    }
    store.$state.username = uuid()
    return true
}

const paymentHandler = async (paymentMethodId: string, stripeCustomerId?: string) => {
    if (!cart.$state.bundle) {
        return
    }
    state.showProgressModal = true
    setTimeout(() => {
        state.paymentComplete = true
    }, 500)
    try {
        const checkoutParams = store.getCheckoutParams()
        const params: BundlePurchaseParams = {
            ...checkoutParams,
            wc_id: cart.$state.bundle.wc_id,
            payment_method: paymentMethodId || store.$state.paymentIntent?.payment_method,
            payment_intent_id: store.$state.paymentIntent?.id,
            stripe_customer_id: stripeCustomerId,
            is_bundle: true,
        }
        const bundleOrder = await bundlePurchase(params)
        state.equipmentComplete = true
        state.subscriptionComplete = true
        const wr_login_data = {
            username: bundleOrder.username,
            account_id: bundleOrder.account_id,
            membership_details: bundleOrder.membership_details,
            order_id: bundleOrder.order_id,
            redirect_url: bundleOrder.redirect_url,
            email: store.$state.email,
        }
        handleTracking(wr_login_data, bundleOrder.order)
        try {
            localStorage.setItem('wr_login_data', JSON.stringify(wr_login_data || '{}'))
            const loginResponse = await userStore.login({
                username: store.$state.email,
                password: store.$state.password,
            })
            if (loginResponse?.access_token) {
                localStorage.setItem('access_token', loginResponse.access_token)
            }
            await userStore.userInfo()
        } catch(e) {
            //
        }
        await finalizeOrder()
        return true
    } catch (e: any) {
        resetProgress()
        if (e?.message && typeof e.message === 'string') {
            try {
                const message = JSON.parse(e.message || '{}')
                if (message?.message) {
                    throw new Error(message.message)
                }
            } catch (error) {
                //throw e
            }
        } else if (e?.message && typeof e.message !== 'string') {
            console.error(e)
            throw new Error('Something went wrong. Please contact support')
        }
        throw e
    }
}
</script>
